/**
 * TimeEntryPage — "Registro de Jornada"
 *
 * Simplified check-in/check-out page for technicians.
 * No real-time timer — just shows start time and calculated elapsed.
 * Includes "forgot to start" (late entry) flow with mandatory reason.
 *
 * @module pages/technician/time/TimeEntryPage
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Card,
  Button,
  Form,
  Spinner,
  Alert,
  Modal,
} from 'react-bootstrap';
import { useAuth } from '../../../contexts/AuthContext';
import {
  checkIn,
  checkOut,
  lateCheckIn,
  getActiveEntry,
  getTodayEntries,
  getEntries,
} from '../../../services/timeEntryService';
import { getAssignedProjects, getProjectDetail } from '../../../services/technicianProjectService';
import { PROJECT_STATUS } from '../../../utils/constants';
import TimeEntryHistory from './TimeEntryHistory';
import './TimeEntryPage.css';

/**
 * Format hours for display
 */
const formatHours = (hours) => {
  if (!hours || hours === 0) return '0h';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

/**
 * Format time from ISO string
 */
const formatTime = (isoString) => {
  if (!isoString) return '--:--';
  return new Date(isoString).toLocaleTimeString('es-CR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Calculate elapsed time string from a start time
 */
const getElapsedString = (startIso) => {
  if (!startIso) return '';
  const diffMs = Date.now() - new Date(startIso).getTime();
  const h = Math.floor(diffMs / 3600000);
  const m = Math.floor((diffMs % 3600000) / 60000);
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
};

const TimeEntryPage = () => {
  const { user } = useAuth();

  // Data state
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [activeEntry, setActiveEntry] = useState(null);
  const [todayEntries, setTodayEntries] = useState([]);
  const [todayTotalHours, setTodayTotalHours] = useState(0);
  const [weekEntries, setWeekEntries] = useState([]);

  // Form state
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedTask, setSelectedTask] = useState('');
  const [projects, setProjects] = useState([]);
  const [projectDetail, setProjectDetail] = useState(null);

  // Late entry modal
  const [showLateModal, setShowLateModal] = useState(false);
  const [lateTime, setLateTime] = useState('');
  const [lateReason, setLateReason] = useState('');

  // GPS state
  const [gpsStatus, setGpsStatus] = useState(null); // null | 'loading' | {latitude, longitude, accuracy} | 'error'

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const startOfWeek = new Date();
      startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
      startOfWeek.setHours(0, 0, 0, 0);

      const [activeRes, todayRes, weekRes, projectsRes] = await Promise.all([
        getActiveEntry(),
        getTodayEntries(),
        getEntries({ from: startOfWeek.toISOString() }),
        // Issue #296: 'active' no longer exists in the live status set; the
        // in-execution status is now 'in_progress'.
        getAssignedProjects(user.id, { status: PROJECT_STATUS.IN_PROGRESS }),
      ]);

      if (activeRes.data) {
        setActiveEntry(activeRes.data);
        if (activeRes.data.project_id) setSelectedProject(activeRes.data.project_id);
        if (activeRes.data.task_id) setSelectedTask(activeRes.data.task_id);
      }

      setTodayEntries(todayRes.data || []);
      setTodayTotalHours(todayRes.totalHours || 0);
      setWeekEntries(weekRes.data || []);
      if (projectsRes.data) setProjects(projectsRes.data);
    } catch (err) {
      console.error('[TimeEntryPage] Load error:', err);
      setError('Error al cargar datos.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Load initial data
  useEffect(() => {
    if (user?.id) loadInitialData();
  }, [user?.id, loadInitialData]);

  // Load project detail when selected
  useEffect(() => {
    if (selectedProject) {
      loadProjectDetail(selectedProject);
    } else {
      setProjectDetail(null);
      setSelectedTask('');
    }
  }, [selectedProject]);

  // Capture GPS on mount
  useEffect(() => {
    if (!navigator.geolocation) {
      setGpsStatus('error');
      return;
    }
    setGpsStatus('loading');
    navigator.geolocation.getCurrentPosition(
      (pos) => setGpsStatus({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      }),
      () => setGpsStatus('error'),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const loadProjectDetail = async (projectId) => {
    try {
      const { data } = await getProjectDetail(projectId);
      setProjectDetail(data);
      setSelectedTask('');
    } catch (err) {
      console.error('[TimeEntryPage] Project detail error:', err);
    }
  };

  // --- Handlers ---

  const handleCheckIn = async () => {
    if (!selectedProject) {
      setError('Seleccione un proyecto antes de iniciar.');
      return;
    }
    setActionLoading(true);
    setError(null);
    try {
      const { data, error: err } = await checkIn({
        project_id: selectedProject,
        task_id: selectedTask || null,
      });
      if (err) throw err;
      setActiveEntry(data);
      setSuccess('Jornada iniciada');
      setTimeout(() => setSuccess(null), 3000);
      // Refresh today
      const todayRes = await getTodayEntries();
      setTodayEntries(todayRes.data || []);
      setTodayTotalHours(todayRes.totalHours || 0);
    } catch (err) {
      setError(err.message || 'Error al iniciar jornada');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (!activeEntry) return;
    setActionLoading(true);
    setError(null);
    try {
      const { data, error: err } = await checkOut({ entry_id: activeEntry.id });
      if (err) throw err;
      setActiveEntry(null);
      const hours = data?.total_hours ? `${data.total_hours}h` : '';
      setSuccess(`Jornada finalizada. ${hours}`);
      setTimeout(() => setSuccess(null), 5000);
      await loadInitialData();
    } catch (err) {
      setError(err.message || 'Error al finalizar jornada');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLateCheckIn = async () => {
    if (!selectedProject) {
      setError('Seleccione un proyecto antes de iniciar.');
      return;
    }
    if (!lateTime) {
      setError('Seleccione la hora de inicio.');
      return;
    }
    if (!lateReason.trim()) {
      setError('El motivo es obligatorio para registros tardíos.');
      return;
    }

    // Build ISO from today + selected time
    const [hours, minutes] = lateTime.split(':');
    const checkInDate = new Date();
    checkInDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    setActionLoading(true);
    setError(null);
    try {
      const { data, error: err } = await lateCheckIn({
        project_id: selectedProject,
        task_id: selectedTask || null,
        check_in_time: checkInDate.toISOString(),
        reason: lateReason.trim(),
      });
      if (err) throw err;
      setActiveEntry(data);
      setShowLateModal(false);
      setLateTime('');
      setLateReason('');
      setSuccess('Jornada registrada (entrada tardía)');
      setTimeout(() => setSuccess(null), 3000);
      const todayRes = await getTodayEntries();
      setTodayEntries(todayRes.data || []);
      setTodayTotalHours(todayRes.totalHours || 0);
    } catch (err) {
      setError(err.message || 'Error al registrar entrada tardía');
    } finally {
      setActionLoading(false);
    }
  };

  // Week total hours
  const weekTotalHours = useMemo(() => {
    return (weekEntries || [])
      .filter(e => e.total_hours)
      .reduce((sum, e) => sum + Number(e.total_hours), 0);
  }, [weekEntries]);

  if (loading) {
    return (
      <Container fluid className="technician-page time-entry-page">
        {/* "Cargando..." a pantalla completa no dice que se esta cargando ni
            cuanto falta. Nombrar el contenido es gratis y orienta. */}
        <div className="time-entry-loading" role="status" aria-live="polite">
          <Spinner animation="border" variant="primary" aria-hidden="true" />
          <span>Cargando tu jornada...</span>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="technician-page time-entry-page">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Registro de Jornada</h1>
        <p className="page-subtitle">
          {activeEntry ? 'Jornada activa' : 'Sin jornada activa'}
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <i className="bi bi-exclamation-circle me-2"></i>{error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          <i className="bi bi-check-circle me-2"></i>{success}
        </Alert>
      )}

      {/* Stats */}
      <div className="stats-section">
        <div className="stats-row">
          <Card className="stat-card">
            <Card.Body>
              <i className="bi bi-sun stat-icon text-warning"></i>
              <span className="stat-value">{formatHours(todayTotalHours)}</span>
              <span className="stat-label">Hoy</span>
            </Card.Body>
          </Card>
          <Card className="stat-card">
            <Card.Body>
              <i className="bi bi-calendar-week stat-icon text-primary"></i>
              <span className="stat-value">{formatHours(weekTotalHours)}</span>
              <span className="stat-label">Semana</span>
            </Card.Body>
          </Card>
          <Card className="stat-card">
            <Card.Body>
              <i className="bi bi-list-check stat-icon text-success"></i>
              <span className="stat-value">{todayEntries.filter(e => e.check_out_time).length}</span>
              <span className="stat-label">Completados</span>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Main Check-in/Check-out Card */}
      <div className="checkin-section">
        <Card className="checkin-card">
          <Card.Body>
            {!activeEntry ? (
              /* ===== INACTIVE STATE ===== */
              <>
                {/* GPS Status */}
                <div className="gps-inline-status mb-3">
                  {gpsStatus === 'loading' && (
                    <div className="gps-status-row">
                      <Spinner animation="border" size="sm" className="me-2" />
                      <span className="text-muted">Obteniendo ubicacion...</span>
                    </div>
                  )}
                  {gpsStatus === 'error' && (
                    <div className="gps-status-row text-warning">
                      <i className="bi bi-geo-alt me-2"></i>
                      <span>GPS no disponible</span>
                    </div>
                  )}
                  {gpsStatus && typeof gpsStatus === 'object' && (
                    <div className="gps-status-row text-success">
                      <i className="bi bi-geo-alt-fill me-2"></i>
                      <span>Ubicacion capturada ({Math.round(gpsStatus.accuracy)}m)</span>
                    </div>
                  )}
                </div>

                {/* Project Selector */}
                <div className="project-selector">
                  <Card className="selector-card">
                    <Card.Body>
                      <div className="mb-3">
                        <div className="selector-label">Proyecto</div>
                        <Form.Select
                          value={selectedProject}
                          onChange={(e) => setSelectedProject(e.target.value)}
                          disabled={actionLoading}
                        >
                          <option value="">Seleccionar proyecto...</option>
                          {projects.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.project_number} - {p.title}
                            </option>
                          ))}
                        </Form.Select>
                      </div>

                      {projectDetail?.tasks?.length > 0 && (
                        <div>
                          <div className="selector-label">Tarea (opcional)</div>
                          <Form.Select
                            value={selectedTask}
                            onChange={(e) => setSelectedTask(e.target.value)}
                            disabled={actionLoading}
                          >
                            <option value="">Seleccionar tarea...</option>
                            {projectDetail.tasks.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.task_number} - {t.title}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </div>

                {/* Check-In Button */}
                <Button
                  className="checkin-btn btn-checkin mt-3"
                  onClick={handleCheckIn}
                  disabled={actionLoading || !selectedProject}
                >
                  {actionLoading ? (
                    <><Spinner animation="border" size="sm" /> <span>Registrando...</span></>
                  ) : (
                    <><i className="bi bi-play-fill"></i> <span>Iniciar Jornada</span></>
                  )}
                </Button>

                {/* Late entry link */}
                <div className="late-entry-link mt-3 text-center">
                  <Button
                    variant="link"
                    className="text-muted"
                    onClick={() => setShowLateModal(true)}
                  >
                    <i className="bi bi-clock-history me-1"></i>
                    Olvide iniciar mi jornada
                  </Button>
                </div>
              </>
            ) : (
              /* ===== ACTIVE STATE ===== */
              <>
                <div className="active-jornada">
                  <div className="active-jornada-icon">
                    <i className="bi bi-check-circle-fill"></i>
                  </div>
                  <div className="active-jornada-title">Jornada iniciada</div>

                  <div className="active-jornada-details">
                    <div className="detail-row">
                      <i className="bi bi-clock me-2"></i>
                      <span>Inicio: {formatTime(activeEntry.check_in_time)}</span>
                    </div>

                    {activeEntry.check_in_latitude && (
                      <div className="detail-row">
                        <i className="bi bi-geo-alt-fill me-2"></i>
                        <span>
                          GPS: {Number(activeEntry.check_in_latitude).toFixed(4)}, {Number(activeEntry.check_in_longitude).toFixed(4)}
                          {activeEntry.check_in_accuracy && ` (${Math.round(Number(activeEntry.check_in_accuracy))}m)`}
                        </span>
                      </div>
                    )}

                    <div className="detail-row">
                      <i className="bi bi-hourglass-split me-2"></i>
                      <span>Tiempo: {getElapsedString(activeEntry.check_in_time)}</span>
                    </div>

                    {activeEntry.project && (
                      <div className="detail-row">
                        <i className="bi bi-folder me-2"></i>
                        <span>{activeEntry.project.project_number} - {activeEntry.project.title}</span>
                      </div>
                    )}

                    {activeEntry.task && (
                      <div className="detail-row">
                        <i className="bi bi-list-task me-2"></i>
                        <span>{activeEntry.task.title}</span>
                      </div>
                    )}

                    {activeEntry.is_late_entry && (
                      <div className="detail-row text-warning">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        <span>Registro tardio</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Check-Out Button */}
                <Button
                  className="checkin-btn btn-checkout mt-3"
                  onClick={handleCheckOut}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <><Spinner animation="border" size="sm" /> <span>Finalizando...</span></>
                  ) : (
                    <><i className="bi bi-stop-fill"></i> <span>Finalizar Jornada</span></>
                  )}
                </Button>
              </>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Today's History */}
      <div className="mb-3">
        <h5 className="mb-2">
          <i className="bi bi-clock-history me-2"></i>
          Historial de Hoy
        </h5>
        <TimeEntryHistory
          entries={todayEntries.filter(e => e.check_out_time)}
          showWeekSummary={false}
          emptyMessage="No hay registros completados hoy"
        />
      </div>

      {/* Week History */}
      <div className="mb-3">
        <h5 className="mb-2">
          <i className="bi bi-calendar-week me-2"></i>
          Esta Semana
        </h5>
        <TimeEntryHistory
          entries={weekEntries.filter(e => e.check_out_time)}
          showWeekSummary={true}
          emptyMessage="No hay registros esta semana"
        />
      </div>

      {/* Late Entry Modal */}
      <Modal
        show={showLateModal}
        onHide={() => { setShowLateModal(false); setLateTime(''); setLateReason(''); setError(null); }}
        centered
        className="technician-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-clock-history me-2"></i>
            Registro Tardio
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Registre su hora de inicio real. Este registro sera marcado como entrada tardia.
          </p>

          {/* Project selector if not already selected */}
          {!selectedProject && (
            <Form.Group className="mb-3">
              <Form.Label>Proyecto</Form.Label>
              <Form.Select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
              >
                <option value="">Seleccionar proyecto...</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.project_number} - {p.title}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Hora de inicio real</Form.Label>
            <Form.Control
              type="time"
              value={lateTime}
              onChange={(e) => setLateTime(e.target.value)}
            />
            <Form.Text className="text-muted">
              Maximo 4 horas atras
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Motivo <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Por que no inicio a tiempo..."
              value={lateReason}
              onChange={(e) => setLateReason(e.target.value)}
            />
          </Form.Group>

          <Alert variant="info" className="mb-0">
            <i className="bi bi-info-circle me-2"></i>
            La ubicacion GPS se captura en este momento. Este registro sera visible para el administrador como &quot;entrada tardia&quot;.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => { setShowLateModal(false); setLateTime(''); setLateReason(''); }}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleLateCheckIn}
            disabled={actionLoading || !lateTime || !lateReason.trim() || !selectedProject}
          >
            {actionLoading ? (
              <><Spinner animation="border" size="sm" className="me-2" /> Registrando...</>
            ) : (
              'Registrar'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default TimeEntryPage;
